/**
 * The player shell (01 data flow, 03 §4 render loop and §9 lifecycle, 04, 09 §9).
 *
 * Boot: loading screen → stage measure → bundle (chunks around the start time first) → GL core,
 * HUD, clock → the view → render loop. The view module is preloaded in the background together
 * with the bundle so the first mount does not wait on the network.
 *
 * Per frame: `clock.tick` → `stateAt` → `style.update` → GL (controller, camera, tiles) and
 * `scene.render()` → `hud.flush()` → chrome readouts.
 */
import type { AppState, Bundle, CommentaryFile, FrameState, Mode, RadioFile, ScaleState, StyleContext, StyleLayout, StyleModule, ViewPreset } from '../core/api.ts';
import { FLAG, flagBitError, MAX_CARS } from '../core/api.ts';
import { AudioEngine } from '../core/audio/audio-engine.ts';
import { EventSchedule, intensityOf } from '../core/audio/ambience.ts';
import { pickTake, readLastTake, takeCount, takeOf, writeLastTake } from '../core/audio/takes.ts';
import type { AudioCar } from '../core/audio/types.ts';
import { loadBundle } from '../core/bundle/loader.ts';
import { createGl, type GlCore } from '../core/gl/index.ts';
import { fitPreset, MPP_MAX } from '../core/gl/projection.ts';
import { pixelFonts, type PixelFonts } from '../core/hud/font.ts';
import { Hud } from '../core/hud/hud.ts';
import { RenderScaleGovernor } from '../core/layout/governor.ts';
import { BUDGET_30, LiteLadder, RafBudget, parseLiteSetting, type LiteSetting } from '../core/layout/lite.ts';
import { FreeTitleCardPolicy } from '../core/style/titleCard.ts';
import { Palette } from '../core/style/theme.ts';
import { fmtRaceClock } from '../core/state/format.ts';
import { suspensionShift, suspensionsOf, type Suspension } from '../core/time/suspension.ts';
import { neutralApiOf, neutralPeriods } from '../core/state/neutral.ts';
import { createFrameState, gone, stateAt } from '../core/state/stateAt.ts';
import { TrackLut } from '../core/state/track.ts';
import { Clock, PRESENTATION_FPS } from '../core/time/clock.ts';
import { TimeWarp } from '../core/time/timewarp.ts';
import { setLastPointerTouch } from '../core/pointer.ts';
import { ChromeController, publishFontVars } from './chrome.ts';
import { audioLabels, CaptionController } from './captions.ts';
import { skipStart, StartScreen } from './start-screen.ts';
import { bindKeyboard } from './keyboard.ts';
import { AudioNotice, DebugOverlay, FrameStats, HelpOverlay, LandscapeGate, LiteNotice, LoadingScreen } from './overlays.ts';
import type { ChromeScale } from './pixel-label.ts';
import { pipsFromEvents, ScrubBar, type Pip } from './scrubbar.ts';
import { raceDisplayLine } from './races.ts';
import { scaleStateOf, StageController, type StageLayout } from './stage.ts';
import { DEFAULT_STATE, HashWriter, parseHash, Store, type DeepLink } from './store.ts';
import { Transport } from './transport.ts';
import { SheetController } from './sheet.ts';
import { PipDrag, readPipCorner, writePipCorner } from './pip-drag.ts';
import type { Corner } from '../core/hud/north-arrow.ts';
import { joinUrl } from '../core/bundle/pump.ts';
import { isModelledSpeed, isReconstructed, provenanceOf } from '../core/provenance.ts';
import { VERSION } from '../version.ts';
import { CatalogError, checkMinPlayer, fetchCatalog, formatBootError, getTilesBase, resolveRaceWithFallback, type Catalog } from './catalog.ts';

declare const __DATA_BASE__: string;
export const DATA_BASE: string = typeof __DATA_BASE__ !== 'undefined' ? __DATA_BASE__ : '/data';

export interface PlayerOptions {
  dataBase?: string;
}

/** The race the legacy (no-catalog) bundle at BUNDLE_URL actually is: deep links to any other race
 *  must fail loudly instead of showing Monza under the wrong label (M8). */
export const LEGACY_RACE_ID = '2024-monza';
export const BUNDLE_URL = `/races/${LEGACY_RACE_ID}/v1`;
/** Race switch from the start screen's picker: only the race id survives (t / style / mode / follow / view
 *  are dropped); a full reload re-boots — `GlCore.useBundle` is one-shot per page load. */
export function switchRace(id: string, loc: { hash: string; reload(): void } = location): void {
  loc.hash = `#race=${id}`;
  loc.reload();
}
/** Camera fly to the fitted preset on a view reset (04). */
export const RESET_FLY_S = 0.4;
const RATES = [1, 5, 20, 60] as const;
/** Fix 3: while BUFFERING, `bundle.retry()` runs on this backoff: first wait 2 s, doubling to a
 *  15 s cap; the delay resets when buffering ends and the window `online` event retries at once. */
const CHUNK_RETRY_BASE_MS = 2000;
const CHUNK_RETRY_MAX_MS = 15000;

type LoadedBundle = Bundle;

/** The one view module; loaded lazily so the shell paints before the view code. */
function loadStyle(): Promise<StyleModule> {
  return import('../styles/undercut/index.ts').then((m) => m.default);
}

/** mulberry32 (05 determinism: seeded RNG only). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface PlayerElements {
  app: HTMLElement;
  stage: HTMLElement;
  gl: HTMLCanvasElement;
  hud: HTMLCanvasElement;
}

/** Test/debug hooks exposed as `window.__undercut` (merged with the view's `UndercutProbe`). */
const LITE_KEY = 'undercut.lite';

/** The remembered LITE choice; storage can be blocked, then it is auto. */
function readLiteSetting(): LiteSetting {
  try {
    return parseLiteSetting(localStorage.getItem(LITE_KEY)) ?? 'auto';
  } catch {
    return 'auto';
  }
}

export interface PlayerProbe {
  state(): Record<string, unknown>;
  carStage(car: number): [number, number] | null;
  worldToStage(x: number, y: number): [number, number];
  stageToWorld(x: number, y: number): [number, number];
  seek(t: number): void;
  /** Director: seek to a video time (v6 frame k at k / 30), e.g. the end hold */
  seekVideo(v: number): void;
  setPlaying(p: boolean): void;
  setMode(m: Mode): void;
  follow(car: number | null): void;
  /** sound / CC toggles and what the captions show (ids), for tests */
  audio(): { sound: boolean; cc: boolean; radio: string | null; commentary: string | null; start: 'hidden' | 'shown' | 'started' | 'switch'; ambience: boolean };
  frameMs(): { mean: number; p95: number; frames: number };
  resetFrameStats(): void;
  /** live GPU/HUD resource counts (leak checks) */
  /** readiness signals: rendered/ticked frame counts, camera idle events, tiles idle, clocked wall time */
  signals(): { frames: number; renders: number; idleCount: number; framesSinceCameraChange: number; msSinceCameraChange: number; flying: boolean; tilesIdle: boolean; clocked: number; buffering: boolean };
  resources(): { meshes: number; materials: number; textures: number; engineTextures: number; activeCameras: number; hudPanels: number; textRuns: number; heap: number | null };
}

export interface ReadyMarkerDoc {
  documentElement: {
    dataset: Record<string, string | undefined>;
  };
}

/** Clear data-ready="error" before re-running boot on Retry. */
export function clearReadyMarker(doc: ReadyMarkerDoc = document): void {
  delete doc.documentElement.dataset.ready;
}

/** Set data-ready="error" when boot/retry fails. */
export function setReadyErrorMarker(doc: ReadyMarkerDoc = document): void {
  doc.documentElement.dataset.ready = 'error';
}

/**
 * Audit 2 fix 4: does this boot failure latch the progress guard? A still-streaming pump must not
 * rewrite the error card — but an abort (a race-picker reload) must not latch it onto the next
 * race's loading screen. boot() clears the guard at its top, so a Retry lets progress flow again.
 */
export function latchesBootError(e: unknown): boolean {
  return (e as { name?: string } | null | undefined)?.name !== 'AbortError';
}

/**
 * Execute the retry runner: clears the ready error marker, then invokes boot().
 * If boot() rejects, logs and re-marks data-ready="error".
 */
export function runRetry(
  bootFn: () => Promise<void>,
  doc: ReadyMarkerDoc = document,
): Promise<void> {
  clearReadyMarker(doc);
  return bootFn().catch((e: unknown) => {
    console.error('undercut: boot failed', e);
    setReadyErrorMarker(doc);
  });
}

/**
 * Guard one-time boot setup work (like stage.start() or style preloading) so it runs
 * exactly once across initial boot and any number of retries.
 */
export function createBootOnce(setup: () => void): () => void {
  let done = false;
  return () => {
    if (done) return;
    done = true;
    setup();
  };
}

export interface BootCatalogResult {
  raceBase: string;
  resolvedRace: string;
  isLegacy: boolean;
  /** the catalog, when one was readable (the `?` race list); null on the legacy local path */
  catalog: Catalog | null;
}

/**
 * Resolve the race data root from the catalog. Gating schema_too_new and min_player errors
 * surfaces an update-the-player card even in the local /data legacy-fallback branch.
 */
export async function resolveBootCatalog(
  dataBase: string,
  deepRace?: string,
  fetchFn: (base: string) => Promise<Catalog> = fetchCatalog,
  bundleUrl = BUNDLE_URL,
): Promise<BootCatalogResult> {
  try {
    const catalog = await fetchFn(dataBase);
    checkMinPlayer(VERSION, catalog.min_player);
    const { race, fellBack } = resolveRaceWithFallback(catalog, deepRace);
    if (fellBack) {
      console.warn(
        `[undercut] unknown race "${deepRace ?? ''}" in the hash; ` +
        `falling back to catalog default_race "${catalog.default_race}"`,
      );
    }
    return {
      resolvedRace: race.id,
      raceBase: joinUrl(dataBase, `races/${race.id}/${race.build}`),
      isLegacy: false,
      catalog,
    };
  } catch (catErr) {
    if (catErr instanceof CatalogError && (catErr.kind === 'min_player' || catErr.kind === 'schema_too_new')) {
      throw catErr;
    }
    if (dataBase === '/data') {
      // Legacy local bundle: it IS Monza. A #race= pointing anywhere else must show LOAD FAILED,
      // not Monza under the wrong label (the pre-M8 bug).
      const raceId = deepRace ?? LEGACY_RACE_ID;
      if (raceId !== LEGACY_RACE_ID) {
        throw new CatalogError(`race "${raceId}" needs the data host / catalog`, 'legacy_race');
      }
      return {
        resolvedRace: raceId,
        raceBase: bundleUrl,
        isLegacy: true,
        catalog: null,
      };
    }
    throw catErr;
  }
}

/** The boot MAX_CARS guard: null when the bundle fits, else the fail-loud message. */
export function maxCarsError(nCars: number): string | null {
  return nCars > MAX_CARS
    ? `race bundle has ${String(nCars)} cars; this player supports up to ${String(MAX_CARS)} — update the player`
    : null;
}

export class Player {
  private readonly store: Store<AppState>;
  private readonly stage: StageController;
  private readonly loading: LoadingScreen;
  private readonly gate: LandscapeGate;
  private readonly help: HelpOverlay;
  private readonly debug: DebugOverlay;
  private readonly notice: AudioNotice;
  private readonly stats = new FrameStats(240);
  private readonly hashWriter: HashWriter;
  private chrome: ChromeController | null = null;
  private transport: Transport | null = null;
  /** the portrait bottom sheet's UI state and touch layer (r14 P6–P8); hidden in landscape */
  private readonly sheet = new SheetController({
    relayout: () => {
      this.relayoutSheet();
    },
    redraw: () => {
      this.hud?.invalidate('sheet');
    },
    tap: (x, y) => this.hud?.pointer(x, y, 'click').clicked ?? false,
  });
  /** the viewer's battle-cam corner in portrait (r141, remembered per race; null = the race preset's) */
  private pipCorner: Corner | null = null;
  private readonly pipDrag = new PipDrag((c) => {
    this.pipCorner = c;
    writePipCorner(this.store.get().race ?? '', c);
    this.relayoutSheet();
  });
  private scrub: ScrubBar | null = null;
  private bundle: LoadedBundle | null = null;
  private gl: GlCore | null = null;
  private hud: Hud | null = null;
  private fonts: PixelFonts | null = null;
  private clock: Clock | null = null;
  private warp: TimeWarp | null = null;
  private track: TrackLut | null = null;
  private fs: FrameState | null = null;
  private ctx: StyleContext | null = null;
  private style: StyleModule | null = null;
  private layout: StyleLayout | null = null;
  private pips: Pip[] = [];
  private suspensions: readonly Suspension[] = [];
  private palette: Palette | null = null;
  private governor: RenderScaleGovernor | null = null;
  private renderScale = 1;
  private pinnedR: number | null = null;
  // LITE (r14): the measured ladder above the governor. `liteSetting` is the remembered choice (query > hash >
  // localStorage > auto); UNDO on the switch-on notice turns it off for the session only (`liteUndone`).
  private liteSetting: LiteSetting = 'auto';
  private liteUndone = false;
  private readonly lite = new LiteLadder();
  private readonly rafBudget = new RafBudget();
  /** the rung the style reads (`ctx.lite`) */
  private readonly liteCtx = { rung: 0 };
  private liteNotice: LiteNotice | null = null;
  private lastRaf = 0;
  private readonly liteIn = { budgetMs: 0, atCap: true, lowerCost: 1 };
  private lastWall = 0;
  private presentation = 0;
  // render on demand (paused + idle = no GL work) and readiness signals for tests
  private glDirty = true;
  private lastRender = { t: Number.NaN, cam: -1, tiles: -1, wall: 0, playing: false };
  private frames = 0;
  private renders = 0;
  private idleCount = 0;
  private camChange = { frame: 0, wall: 0 };
  /** Σ wall dt applied to the clock while playing (clamped), for rate-independent checks */
  private clocked = 0;
  /** debug: busy-wait this many ms per frame (`?stall=600` reproduces slow CI swiftshader) */
  private stallMs = 0;
  private userMoved = false;
  /** a fly to the (fitted) preset is running: wall start and duration */
  private presetFly: { start: number; dur: number } | null = null;
  private buffering = false;
  /** fix 3: next `bundle.retry()` time (performance.now base) while BUFFERING; 0 = next frame */
  private nextRetryAt = 0;
  /** fix 3: current chunk-retry wait: doubles per retry, capped, reset when buffering ends */
  private retryDelayMs = CHUNK_RETRY_BASE_MS;
  private retiredMask = 0;
  // race audio: CC is on at every load (no hash or storage persistence). Sound is off until the START click
  // (SND shows pressed meanwhile: `soundIntent`); with `nostart=1` it stays off, as in the tests.
  private audio: AudioEngine | null = null;
  private soundOn = false;
  private soundIntent = true;
  private startScreen: StartScreen | null = null;
  /** was the race playing when SWITCH RACE opened the picker (RESUME restores it) */
  private switchResume = false;
  private eventSchedule: EventSchedule | null = null;
  private noticeShown = false;
  private captions: CaptionController | null = null;
  private files: { radio: RadioFile | null; commentary: CommentaryFile | null } = { radio: null, commentary: null };
  // commentary take (C10), 0-based; pinned = the URL carries it (a deep link or the START pick),
  // so it is written back into the hash and the START gesture does not pick again
  private take = 0;
  private takePinned = false;
  private audioCars: AudioCar[] = [];
  private readonly scratch = [0, 0];
  private tEnd = 0;
  private laps = 53;
  private deep: DeepLink;
  private firstLayout: Promise<StageLayout>;
  private ready = false;
  /** fix 4: boot() failed after the bundle started loading: the card shows LOAD FAILED and the
   *  still-streaming chunk-progress events (the pump is not stopped by a failed boot) must not
   *  overwrite it. Cleared at the top of boot(), so a Retry's progress flows again. */
  private bootError = false;
  private readonly dataBase: string;

  constructor(private readonly el: PlayerElements, opts: PlayerOptions = {}) {
    this.dataBase = opts.dataBase ?? DATA_BASE;
    this.deep = parseHash(location.hash);
    // `take=<n>` in the hash (C10): 1-based, and parseHash only lets integers >= 1 through; a take
    // beyond K is dropped when the commentary file arrives (K is known only then)
    if (this.deep.take !== undefined) {
      this.take = this.deep.take - 1;
      this.takePinned = true;
    }
    const q = new URLSearchParams(location.search);
    const pin = q.get('r');
    if (pin !== null && Number(pin) > 0) this.pinnedR = Number(pin);
    const stall = Number(q.get('stall'));
    if (stall > 0) this.stallMs = Math.min(stall, 5000);
    this.renderScale = this.pinnedR ?? 1;
    this.liteSetting = parseLiteSetting(q.get('lite')) ?? this.deep.lite ?? readLiteSetting();
    this.lite.setMode(this.liteSetting);
    this.liteCtx.rung = this.lite.rung;
    this.store = new Store<AppState>({
      ...DEFAULT_STATE,
      race: this.deep.race,
      raceTime: this.deep.t ?? 0,
      mode: this.deep.mode ?? DEFAULT_STATE.mode,
    });
    this.loading = new LoadingScreen(document.body);

    this.help = new HelpOverlay(document.body, () => this.bundle?.manifest.attribution ?? '', (s) => { this.pickLite(s); });
    this.liteNotice = new LiteNotice(document.body, () => {
      this.liteUndone = true;
      this.lite.setMode('off');
      this.syncLite();
    });
    this.help.setLite(this.liteSetting, this.lite.rung);
    this.gate = new LandscapeGate(document.body);
    this.debug = new DebugOverlay(document.body);
    this.notice = new AudioNotice(document.body);
    if (!skipStart(location.search)) {
      this.startScreen = new StartScreen(document.body, el.app, () => { this.startFromScreen(); }, (open) => {
        // r142: SWITCH RACE pauses under the picker; RESUME plays again only if it was playing
        if (open) this.switchResume = this.clock?.playing ?? false;
        this.setPlaying(open ? false : this.switchResume);
      });
    }
    else this.soundIntent = false;
    this.hashWriter = new HashWriter(() => this.deepLink());

    let resolveFirst: (l: StageLayout) => void = () => undefined;
    this.firstLayout = new Promise((res) => {
      resolveFirst = res;
    });
    this.stage = new StageController({
      appEl: el.app,
      stageEl: el.stage,
      renderScale: () => this.renderScale,
      onLayout: (l, prev) => {
        publishFontVars(l.scale.k, l.dpr); // the loading card and the gate need them before the chrome exists
        this.applyGate(l);
        if (!this.ready) {
          resolveFirst(l);
          return;
        }
        this.relayout(l, prev);
      },
    });
  }

  // ───────────────────────────── boot ─────────────────────────────

  /**
   * One-time setup that a Retry must NOT redo (an observed stage would stack observers). Guarded, so boot/retry can call it freely.
   */
  private readonly bootOnce = createBootOnce(() => {
    this.stage.start();
    // fix 3: a reconnect must not wait for the retry backoff to elapse
    window.addEventListener('online', () => {
      this.nextRetryAt = 0;
    });
  });

  /**
   * The loading card's Retry: clear the boot-failure marker, then re-run only the fetch-and-load
   * part (boot-once work is kept). A successful retry ends in boot()'s `data-ready = '1'`; a
   * failed one re-marks the error here.
   */
  private retry(): void {
    void runRetry(() => this.boot());
  }

  async boot(): Promise<void> {
    this.bootOnce();
    this.bootError = false;

    let raceBase: string;
    let resolvedRace: string;
    let isLegacy: boolean;
    let resolvedCatalog: Catalog | null;

    try {
      this.loading.phaseText('CHECKING CATALOG');
      const resolved = await resolveBootCatalog(this.dataBase, this.deep.race);
      raceBase = resolved.raceBase;
      resolvedRace = resolved.resolvedRace;
      isLegacy = resolved.isLegacy;
      resolvedCatalog = resolved.catalog;
    } catch (catErr) {
      if (catErr instanceof CatalogError && (catErr.kind === 'min_player' || catErr.kind === 'schema_too_new')) {
        const msg = catErr.kind === 'min_player'
          ? `UPDATE THE PLAYER: catalog requires player >= ${catErr.minPlayer ?? 'newer'} (running v${VERSION})`
          : `UPDATE THE PLAYER: ${catErr.message}`;
        this.loading.error(msg, () => { this.retry(); });
      } else {
        const msg = formatBootError(catErr);
        this.loading.error(msg, () => { this.retry(); });
      }
      throw catErr;
    }

    this.store.set({ race: resolvedRace });
    this.hashWriter.write();
    // the card names the race as soon as it is known (catalog race, or the legacy bundle's race)
    const catRace = resolvedCatalog?.races.find((r) => r.id === resolvedRace) ?? null;
    this.loading.setRaceLine(raceDisplayLine(catRace ?? { id: resolvedRace }));

    // audit 2 fix 2: a Retry must not double-stream beside the failed load: dispose the old
    // bundle (no render loop is running yet — boot() starts it only at its end) and drop fs
    this.bundle?.dispose();
    this.bundle = null;
    this.fs = null;
    let bundle: LoadedBundle;
    try {
      bundle = await loadBundle(raceBase, {
        startT: this.deep.t ?? 0,
        // H2: the one view (Undercut) draws its own track ribbon and never shows vectors.json
        vectors: false,
        onProgress: (p) => {
          if (!this.bootError) this.loading.progress(p);
        },
      });
    } catch (e) {
      this.bootError = latchesBootError(e);
      const msg = formatBootError(e);
      this.loading.error(msg, () => { this.retry(); });
      throw e;
    }
    this.bundle = bundle;
    const man = bundle.manifest;
    this.help.setReconstruction(provenanceOf(man));   // the help routes by positions value: reconstructed -> the modelled-fields box, tracinginsights -> the TI sentence, null -> FastF1
    // fail loud on a bundle this player cannot decode correctly (M8): flag bits it assumes, or
    // more cars than the GL/scratch arrays hold (MAX_CARS)
    const flErr = flagBitError(man);
    if (flErr) this.failBoot(new Error(flErr));
    const carsErr = maxCarsError(bundle.timeline.nCars);
    if (carsErr) this.failBoot(new Error(carsErr));
    // fix 4: everything after the failBoot checks can still throw (createGl, the first
    // registry.load, mountStyle): the loading card must show the error with a reload, not
    // freeze at its last progress line. Reload, not retry(): GL, listeners and input are
    // already set up here and a second boot() would set them up twice (main.ts stays as is).
    try {
      this.palette = new Palette(man);
      this.fonts = pixelFonts(bundle.fonts);
      this.warp = TimeWarp.fromBundle(bundle);
      this.track = TrackLut.fromBundle(bundle);
      const tl = bundle.timeline;
      this.tEnd = (tl.frames - 1) / tl.hz;
      this.laps = man.race.laps;
      const st = this.store.get();
      this.clock = new Clock(this.warp, { tEnd: this.tEnd, mode: st.mode, raceTime: Math.min(st.raceTime, this.tEnd) });
      this.fs = createFrameState(tl.nCars);

      const l = await this.firstLayout;
      this.applyGovernor(l.scale);
      const scale = scaleStateOf(l.resolved, this.renderScale);
      const gl = (this.gl = createGl(this.el.gl, scale, {
        controller: {
          clickToFollow: false,
          onPick: (car, touch) => {
            if (car !== null) this.setFollow(car);
            // T2: a touch tap that hit no car and no HUD region toggles the chrome (landscape;
            // the portrait layout turns this off — its transport is always visible)
            else if (touch) this.chrome?.tapToggle();
          },
          // TF4: every gesture zoom (pinch, double-tap, wheel, keyboard) marks the view user-moved
          // for the deep link — a touch zoom fires no wheel or dblclick this shell could hook
          onZoom: () => {
            this.userMoved = true;
          },
        },
        onTileError: () => {
          if (!this.ready) {
            this.loading.addNote('map data failed to load');
          }
        },
      }));
      const tilesBase = isLegacy ? raceBase : getTilesBase(this.dataBase, raceBase, man);
      gl.useBundle({ baseUrl: raceBase, tilesBase, vectors: bundle.vectors, tiles: man.tiles });
      const fs = this.fs;
      gl.camera.carSource = {
        count: tl.nCars,
        position: (car: number, out: number[]): boolean => {
          const flags = fs.flags[car] ?? 0;
          if (flags & (FLAG.RETIRED | FLAG.HIDDEN)) return false; // retired / hidden: not on the map
          out[0] = fs.x[car] ?? 0;
          out[1] = fs.y[car] ?? 0;
          return true;
        },
      };
      gl.camera.on('idle', () => {
        this.idleCount++;
      });
      gl.camera.on('change', () => {
        this.camChange.frame = this.frames;
        this.camChange.wall = performance.now();
        this.style?.onCamera?.(this.ctxOrThrow());
        this.hashWriter.request();
      });
      // Babylon makes the canvas focusable in the tab order; keyboard shortcuts live on the document
      this.el.gl.tabIndex = -1;
      const hud = (this.hud = new Hud(this.el.hud, { fonts: this.fonts, now: () => this.presentation }));
      hud.resize({ k: scale.k, stage: scale.stage });

      const pal = this.palette;
      const cap = (this.captions = new CaptionController({
        laps: bundle.laps,
        teamRgb: (code) => {
          const i = pal.carIndex(code);
          return i >= 0 ? pal.teamRgb(i) : null;
        },
        raceOfPres: (v) => this.warp?.raceT(v) ?? 0,
      }));
      this.ctx = {
        captions: cap,
        lite: this.liteCtx,
        sheet: this.sheet,
        engine: gl.engine,
        scene: gl.scene,
        camera: gl.camera,
        bundle,
        clock: this.clock,
        track: this.track,
        hud,
        scale,
        follow: { get: () => this.store.get().follow, set: (c) => { this.setFollow(c); } },
        rng: mulberry32,
        gl,
        titleCard: new FreeTitleCardPolicy(this.clock),
        neutral: neutralApiOf(man),
        requestRender: () => {
          this.glDirty = true;
        },
      };
      this.eventSchedule = new EventSchedule(bundle.events);
      this.audioCars = Array.from({ length: tl.nCars }, (_, idx) => ({ idx, sx: 0, sy: 0, dist: 0, rpm: 0, thr: 0, gear: 0, running: false }));
      // radio.json / commentary.json load in the background; absent or failed = no captions, no error
      void Promise.all([bundle.radio ?? null, bundle.commentary ?? null]).then(([radio, commentary]) => {
        if (this.bundle !== bundle) return;
        this.files = { radio, commentary };
        if (this.take >= takeCount(commentary)) {
          this.take = 0;        // a take= beyond K behaves like no take= at all
          this.takePinned = false;
        }
        this.applyCommentaryFiles();
        document.documentElement.dataset.captions = 'ready'; // tests wait for it: boot itself never does
      }).catch((e: unknown) => {
        // audit 2 fix 9: a background caption failure must never be an unhandled rejection
        console.error('undercut: caption files failed', e);
      });
      // a mode change can change what styles draw while paused (variants by mode)
      this.clock.on('mode', () => {
        this.glDirty = true;
      });

      this.buildChrome(bundle, l);
      // the start screen's race picker: the catalog's races (the legacy bundle has no catalog, so no switch)
      this.startScreen?.setRaces(resolvedCatalog?.races ?? [], resolvedRace, (id) => {
        switchRace(id);
      });
      this.bindInput();
      this.ready = true;

      // the view + deep link
      await this.mountStyle(await loadStyle());
      void bundle.whenReady(this.clock.raceTime).catch(() => undefined);
      const followCode = this.deep.follow;
      if (followCode) {
        const idx = this.palette.carIndex(followCode);
        if (idx >= 0) this.setFollow(idx);
      }
      if (this.deep.view) {
        const v = this.deep.view;
        gl.camera.setRelativeView(v.cx, v.cy, 1 / v.z);
        gl.camera.clampToExtent();
        this.userMoved = true;
      }
      this.relayout(this.stage.layout ?? l, null);
      if (this.deep.play && !this.gate.visible) this.setPlaying(true);

      this.store.subscribe((s, prev) => {
        const discrete = s.race !== prev.race || s.follow !== prev.follow || JSON.stringify(s.mode) !== JSON.stringify(prev.mode) || s.playing !== prev.playing;
        this.hashWriter.request(discrete);
      });
      window.addEventListener('hashchange', () => {
        this.applyDeepLink(parseHash(location.hash));
      });
      document.addEventListener('visibilitychange', () => {
        this.lastWall = performance.now(); // a hidden tab never jumps the clock on return
        // H6: a hidden tab pauses playback (background rAF is throttled to nothing anyway);
        // returning never auto-resumes — the viewer presses play again
        if (document.hidden) this.setPlaying(false);
      });

      this.lastWall = performance.now();
      gl.engine.runRenderLoop(() => {
        if (this.liteSkip()) return;
        this.frame();
      });
      this.frame();
      this.installProbe();
      // ready = first frame rendered and nothing covering the stage any more
      await this.loading.hide();
      this.transport?.setSound(this.soundIntent);
      this.startScreen?.show();
      document.documentElement.dataset.ready = '1';
    } catch (e) {
      this.bootError = true;
      this.loading.error(formatBootError(e), () => {
        location.reload();
      });
      throw e;
    }
  }

  private ctxOrThrow(): StyleContext {
    if (!this.ctx) throw new Error('player not booted');
    return this.ctx;
  }

  /** Fail boot with the card (the caller throws; the outer handler marks data-ready="error"). */
  private failBoot(e: unknown): never {
    this.bootError = latchesBootError(e);
    this.loading.error(formatBootError(e), () => {
      this.retry();
    });
    throw e;
  }

  private buildChrome(bundle: LoadedBundle, l: StageLayout): void {
    const man = bundle.manifest;
    this.pips = pipsFromEvents(bundle.events);
    const suspensions = (this.suspensions = suspensionsOf(man));
    const scrub = (this.scrub = new ScrubBar({
      duration: this.tEnd,
      lapStarts: man.time_base.leader_lap_starts,
      pips: this.pips,
      neutralPeriods: neutralPeriods(man),
      suspensions,
      formatTime: (t) => fmtRaceClock(t + suspensionShift(suspensions, t)),
      onScrub: (t) => {
        const c = this.clock;
        if (!c) return;
        c.seek(t);
        void bundle.whenReady(c.raceTime).catch(() => undefined);
      },
      onSeek: () => {
        const c = this.clock;
        if (c) this.style?.onSeek?.(c.raceTime, this.ctxOrThrow());
      },
      onPipHover: (id) => {
        if (this.ctx) this.style?.onEventHover?.(id, this.ctx);
      },
    }));
    const drivers = man.drivers
      .slice()
      .sort((a, b) => a.position - b.position)
      .map((d) => ({ idx: d.idx, code: d.code, name: d.name, team: d.team, hex: d.hex }));
    const tr = (this.transport = new Transport(
      {
        togglePlay: () => {
          this.togglePlay();
        },
        setMode: (m) => {
          this.setMode(m);
        },
        jumpEvent: (d) => {
          this.jumpEvent(d);
        },
        jumpLap: (d) => {
          this.jumpLap(d);
        },
        follow: (c) => {
          this.setFollow(c);
        },
        resetView: () => {
          this.resetView();
        },
        toggleFullscreen: () => {
          void this.stage.toggleFullscreen();
        },
        toggleHelp: () => {
          this.help.toggle();
        },
        toggleSound: () => {
          this.toggleSound();
        },
        toggleCaptions: () => {
          this.toggleCaptions();
        },
        zoom: (f) => {
          this.gl?.controller.zoomBy(f);
          this.userMoved = true;
        },
        cycleLite: () => {
          const order: readonly LiteSetting[] = ['auto', 'on', 'off'];
          this.pickLite(order[(order.indexOf(this.liteUndone ? 'off' : this.liteSetting) + 1) % order.length] ?? 'auto');
        },
      },
      scrub,
      drivers,
    ));
    this.el.app.appendChild(this.sheet.el);
    this.el.app.appendChild(this.pipDrag.el);
    this.el.app.appendChild(tr.el);
    tr.setLite(this.liteUndone ? 'off' : this.liteSetting);
    this.chrome = new ChromeController(this.el.app, tr.el);
    tr.setAttribution(man.attribution);
    tr.setFonts(this.fonts);
    scrub.setFonts(this.fonts);
    this.placeChrome(l);
  }

  private chromeScale(l: StageLayout): ChromeScale {
    return { k: l.scale.k, dpr: l.dpr };
  }

  private placeChrome(l: StageLayout): void {
    this.chrome?.place(l);
    const sc = this.chromeScale(l);
    this.transport?.setScale(sc);
    this.scrub?.setScale(sc);
  }

  /** The gate (plan §3.6, portrait plan P2): up over a paused player for a sideways phone or a too-small window; a resize into range hides it, nothing resumes playback. */
  private applyGate(l: StageLayout): void {
    const r = l.resolved;
    if (r.rotate || r.tooSmall) {
      this.gate.show(r.rotate ? 'rotate' : 'small');
      this.setPlaying(false);
    } else this.gate.hide();
  }

  // ───────────────────────────── layout ─────────────────────────────

  private applyGovernor(scale: ScaleState): void {
    const f = scale.k;
    if (!this.governor) {
      this.governor = new RenderScaleGovernor({ budgetMs: this.rafBudget.budgetMs, u: f, pin: this.pinnedR });
    } else this.governor.setU(f);
    this.renderScale = this.effectiveR();
  }

  /** r in effect: the governor's, or from LITE rung 2 up to the device pixel ratio (GL at 1 CSS px); a `?r=` pin wins. */
  private effectiveR(): number {
    const g = this.governor?.r ?? this.renderScale;
    if (this.pinnedR !== null) return this.pinnedR;
    return this.lite.rung >= 2 ? Math.max(g, window.devicePixelRatio || 1) : g;
  }

  // ───────────────────────────── LITE ─────────────────────────────

  /** 30 fps cap (rung 1): skip this rAF unless a 30 fps frame interval has passed. Also measures the raw rAF interval (H5). */
  private liteSkip(): boolean {
    const now = performance.now();
    if (this.lastRaf > 0) this.rafBudget.push(now - this.lastRaf);
    this.lastRaf = now;
    this.rafBudget.governorAtCap = this.governor?.atCap ?? false;
    this.lite.setPlatformCapped(this.rafBudget.platformCapped);
    this.syncLite();
    // half a rAF interval of slack: 60 Hz renders every 2nd callback, 120 Hz every 4th, a platform-capped 30 Hz every one
    return this.lite.rung >= 1 && now - this.lastWall < 1000 / PRESENTATION_FPS - this.rafBudget.intervalMs / 2;
  }

  /** Push the ladder's rung to everything that reads it: the style (chip, inset tiles), the help, r, the notice. */
  private syncLite(): void {
    const rung = this.lite.rung;
    if (rung === this.liteCtx.rung) return;
    const was = this.liteCtx.rung;
    this.liteCtx.rung = rung;
    this.glDirty = true;
    if (was === 0 && rung === 1 && this.lite.mode === 'auto' && !this.lite.platformCapped) this.liteNotice?.show();
    this.help.setLite(this.liteUndone ? 'off' : this.liteSetting, rung);
    this.transport?.setLite(this.liteUndone ? 'off' : this.liteSetting);
    const r = this.effectiveR();
    if (r !== this.renderScale && this.ready) {
      this.renderScale = r;
      this.stage.setRenderScale();
    }
  }

  private pickLite(s: LiteSetting, writeHash = true): void {
    this.liteSetting = s;
    this.liteUndone = false;
    try {
      localStorage.setItem(LITE_KEY, s);
    } catch {
      // storage blocked: the choice lasts for this page only
    }
    this.lite.setMode(s);
    this.liteCtx.rung = -1; // force the sync below to run even when the rung is unchanged
    this.syncLite();
    if (writeHash) this.hashWriter.request();
  }

  /** The fitted preset for this layout at the live device scale (crisp levels in device px). */
  private fitted(L: StyleLayout, scale: ScaleState): ViewPreset {
    const style = this.style;
    if (!style || L.isReference || !this.gl) return L.view;
    const opts: { k: number; tileMpps: readonly number[]; frameRect?: ViewPreset['mapRect'] } = { k: scale.k, tileMpps: this.gl.camera.tileMpps };
    if (L.view.frameRect) opts.frameRect = L.view.frameRect;
    const view = fitPreset(L.fitRef ?? style.reference.view, L.mapRect, opts);
    // the layout's reserved-zone nudge (a narrow stage puts more track under the inset) is part of its view
    view.centerPx = [L.view.centerPx[0], L.view.centerPx[1]];
    return view;
  }

  private relayout(l: StageLayout, prev: StageLayout | null): void {
    const gl = this.gl;
    const hud = this.hud;
    const ctx = this.ctx;
    if (!gl || !hud || !ctx) return;
    const t0 = performance.now();
    this.applyGovernor(l.scale);
    const scale = scaleStateOf(l.resolved, this.renderScale);
    ctx.scale = scale;
    gl.applyScale(scale);
    const onlyR = prev !== null && prev.resolved === l.resolved;
    if (!onlyR) {
      hud.resize({ k: scale.k, stage: scale.stage });
      this.placeChrome(l);
    }
    const style = this.style;
    if (style && !onlyR) {
      const L = style.layout(scale.stage, this.sheet.state, this.pipCorner ?? undefined);
      this.layout = L;
      style.onLayout(L, ctx);
      this.placeSheet(l);
      gl.camera.mppMax = L.mppMax ?? MPP_MAX;
      const view = this.fitted(L, scale);
      const fly = this.presetFly;
      if (fly && gl.camera.flying) {
        // a reset fly is running: retarget it to the new fitted preset
        const left = fly.dur - (performance.now() - fly.start) / 1000;
        if (left > 0.02) gl.camera.flyToPreset(view, left);
        else gl.camera.setPreset(view);
      } else gl.camera.applyLayout(view);
      gl.controller.setInput(L.inputRect, L.exclusions);
      gl.gl.setClear(L.clear);
    }
    this.glDirty = true;
    this.frame(0);
    this.debugRelayoutMs = performance.now() - t0;
  }

  private debugRelayoutMs = 0;

  /** The portrait sheet's touch layer and the transport row at its top (landscape: the bar's own place). */
  private placeSheet(l: StageLayout): void {
    const sh = this.layout?.sheet ?? null;
    this.sheet.place(this.layout, l);
    this.el.app.dataset.sheet = sh ? this.sheet.state : '';
    this.transport?.setPortrait(sh !== null);
    this.chrome?.place(l, sh ? sh.rect.y : null);
  }

  /** A new sheet state (UI state, r14): the view lays out again over the same map rect and framing. */
  private relayoutSheet(): void {
    const style = this.style;
    const ctx = this.ctx;
    const gl = this.gl;
    const l = this.stage.layout;
    if (!style || !ctx || !gl || !l) return;
    const L = style.layout(ctx.scale.stage, this.sheet.state, this.pipCorner ?? undefined);
    this.layout = L;
    style.onLayout(L, ctx);
    gl.controller.setInput(L.inputRect, L.exclusions);
    this.placeSheet(l);
    this.glDirty = true;
  }

  // ───────────────────────────── the view ─────────────────────────────

  private async mountStyle(style: StyleModule): Promise<void> {
    const ctx = this.ctxOrThrow();
    const gl = this.gl;
    if (!gl) return;
    const scale = ctx.scale;
    style.setRace?.(ctx.bundle);
    // a race mount (boot, Retry) starts with the sheet at the peek, on TOWER, unscrolled (SF1)
    this.sheet.reset();
    this.pipCorner = readPipCorner(this.store.get().race ?? '');
    const L = style.layout(scale.stage, this.sheet.state, this.pipCorner ?? undefined);
    // fix 7: publish the style only once mount() has resolved — until then `this.style` is null,
    // so frame() never calls update() on a half-mounted (or failed) style
    this.style = null;
    this.layout = null;
    gl.gl.setClear(L.clear);
    this.glDirty = true;
    await style.mount(ctx, L);
    this.style = style;
    this.layout = L;
    const t = this.clock?.raceTime ?? 0;
    style.onSeek?.(t, ctx);
    gl.camera.mppMax = L.mppMax ?? MPP_MAX;
    const view = this.fitted(L, scale);
    gl.camera.setPreset(view);
    this.userMoved = false;
    gl.controller.setInput(L.inputRect, L.exclusions);
    gl.camera.follow(this.store.get().follow);
    const sl = this.stage.layout;
    if (sl) this.placeSheet(sl);
  }

  // ───────────────────────────── time control ─────────────────────────────

  setPlaying(p: boolean): void {
    const c = this.clock;
    if (!c || (p && this.gate.visible)) return; // nothing plays behind the landscape gate
    if (p && c.mode.kind === 'free' && c.raceTime >= this.tEnd) this.seek(0);
    if (p && c.mode.kind === 'director' && c.videoTime >= c.warp.duration - 1e-6) this.seek(0);
    c.setPlaying(p);
    this.store.set({ playing: p });
  }

  togglePlay(): void {
    this.setPlaying(!(this.clock?.playing ?? false));
  }

  setMode(m: Mode): void {
    this.clock?.setMode(m);
    this.store.set({ mode: m });
  }

  stepRate(dir: -1 | 1): void {
    const m = this.clock?.mode;
    if (!m) return;
    const cur = m.kind === 'director' ? 20 : m.rate;
    const i = RATES.indexOf(cur);
    const j = Math.min(RATES.length - 1, Math.max(0, i + dir));
    const rate = RATES[j] ?? 20;
    this.setMode({ kind: 'free', rate });
  }

  seek(t: number): void {
    const c = this.clock;
    if (!c) return;
    c.seek(Math.max(t, 0)); // the clock clamps (free: tEnd, Director: the end-hold race time)
    const tt = c.raceTime;
    void this.bundle?.whenReady(tt).catch(() => undefined);
    this.style?.onSeek?.(tt, this.ctxOrThrow());
    this.store.set({ raceTime: tt });
  }

  private jumpLap(dir: -1 | 1): void {
    const starts = this.bundle?.manifest.time_base.leader_lap_starts;
    const c = this.clock;
    if (!starts || !c) return;
    const t = c.raceTime;
    let i = 0;
    for (let k = 0; k < starts.length; k++) if ((starts[k] ?? 0) <= t + 1e-6) i = k;
    if (dir < 0) {
      const cur = starts[i] ?? 0;
      this.seek(t - cur > 2 ? cur : (starts[Math.max(0, i - 1)] ?? 0));
    } else {
      const nx = starts[i + 1];
      if (nx !== undefined) this.seek(nx);
    }
  }

  /** Previous/next story event: lands 2 s before it so the moment plays. */
  private jumpEvent(dir: -1 | 1): void {
    const c = this.clock;
    if (!c || this.pips.length === 0) return;
    const LEAD = 2;
    const t = c.raceTime + LEAD;
    let target: Pip | undefined;
    if (dir > 0) target = this.pips.find((p) => p.t > t + 0.05);
    else {
      for (let i = this.pips.length - 1; i >= 0; i--) {
        const p = this.pips[i];
        if (p && p.t < t - 0.5) {
          target = p;
          break;
        }
      }
    }
    if (target) this.seek(Math.max(0, target.t - LEAD));
  }

  setFollow(car: number | null): void {
    // a retired car is not on the map: refuse to follow it (the dropdown marks it OUT)
    if (car !== null && this.fs && gone(this.fs.flags[car] ?? 0)) return;
    this.gl?.camera.follow(car);
    this.store.set({ follow: car });
    this.transport?.setFollow(car);
  }

  private resetView(): void {
    this.setFollow(null);
    this.gl?.camera.reset(RESET_FLY_S);
    this.presetFly = { start: performance.now(), dur: RESET_FLY_S };
    this.userMoved = false;
  }

  // ───────────────────────────── race audio ─────────────────────────────

  private ensureAudio(): AudioEngine {
    if (!this.audio) {
      this.audio = AudioEngine.create();
      this.applyAudioFiles();
    }
    return this.audio;
  }

  private applyAudioFiles(): void {
    const a = this.audio, b = this.bundle, w = this.warp;
    if (!a || !b || !w) return;
    a.setRace(b.manifest.race.key);
    a.setRadio(this.files.radio);
    a.setCommentary(takeOf(this.files.commentary, this.take), b.baseUrl, (v) => w.raceT(v));
  }

  /** (Re)apply the race-audio files as the current take (C10): the captions and the audio engine
   *  both read `takeOf(files.commentary, take)` — without `takes` that is the file itself. */
  private applyCommentaryFiles(): void {
    const file = takeOf(this.files.commentary, this.take);
    this.captions?.setFiles(this.files.radio, file);
    this.applyAudioFiles();
    const modelled = this.bundle ? isModelledSpeed(this.bundle.manifest) : false;
    this.help.setAudioLabels(audioLabels(this.files.radio, file, this.take, modelled));
    if (this.bundle) {
      const hosts = [...new Set((file?.race_sources ?? []).map((src) => new URL(src.url).hostname.replace(/^www\./, '')))];
      this.help.setReconstruction(provenanceOf(this.bundle.manifest), hosts);   // positions-keyed routing inside: a tracinginsights race gets the TI sentence, not null (impl-gate F1)
    }
  }

  /** M / the SND button. The AudioContext is created and resumed here, inside the user gesture. */
  toggleSound(): void {
    if (this.startScreen?.state === 'shown') {       // before START only the intent changes: no context yet
      this.soundIntent = !this.soundIntent;
      this.transport?.setSound(this.soundIntent);
      return;
    }
    const a = this.ensureAudio();
    if (this.soundOn) {
      this.soundOn = false;
      a.disable();
    } else {
      this.soundOn = true;
      a.enable().catch(() => {
        this.soundOn = false;
        this.transport?.setSound(false);
      });
      if (!this.noticeShown) {
        this.noticeShown = true;
        this.notice.show(audioLabels(this.files.radio, takeOf(this.files.commentary, this.take), this.take,
          this.bundle ? isModelledSpeed(this.bundle.manifest) : false));
      }
    }
    this.transport?.setSound(this.soundOn);
  }

  /** The START click or key: the gesture that turns sound on (unless muted on the screen) and starts playback. */
  private startFromScreen(): void {
    this.pickTake();
    if (this.soundIntent && !this.soundOn) this.toggleSound();
    this.setPlaying(true);
  }

  /** The START gesture picks the commentary take (C10): a random one, different from the last take
   *  this viewer heard. `nostart=1` never runs this and a `take=` in the URL wins, so tests, goldens
   *  and shared links stay on their take. */
  private pickTake(): void {
    if (this.takePinned) return;
    const k = takeCount(this.files.commentary);
    if (k <= 1) return;
    const raceKey = this.bundle?.manifest.race.key ?? this.store.get().race ?? '';
    const last = readLastTake(raceKey);
    // eslint-disable-next-line no-restricted-properties -- unseeded on purpose (C10): the pick lands in the URL hash, so the take can be replayed
    const take = pickTake(k, last, Math.random);
    writeLastTake(raceKey, take);
    this.take = take;
    this.takePinned = true;
    this.applyCommentaryFiles();
    this.hashWriter.request(true);
  }

  /** C / the CC button: the captions draw only while on (the key change redraws the panel). */
  toggleCaptions(): void {
    const c = this.captions;
    if (!c) return;
    c.on = !c.on;
    this.transport?.setCaptions(c.on);
    this.glDirty = true;
  }

  /** The per-frame engine input (plan P2 rate table): the listener is the battle-cam pair midpoint while the
   *  camera rests on its preset, else the camera centre. */
  private updateAudio(clock: Clock, fs: FrameState): void {
    const a = this.audio, gl = this.gl, l = this.stage.layout;
    if (!this.soundOn || !a || !gl || !l) return;
    const cam = gl.camera;
    const pair = this.store.get().follow === null && cam.isAtPreset() ? this.style?.audioFocus?.() ?? null : null;
    const lx = pair ? ((fs.x[pair[0]] ?? 0) + (fs.x[pair[1]] ?? 0)) / 2 : cam.center[0];
    const ly = pair ? ((fs.y[pair[0]] ?? 0) + (fs.y[pair[1]] ?? 0)) / 2 : cam.center[1];
    const { w, h } = l.scale.stage;
    const o = this.scratch;
    const gapM = pair ? Math.hypot((fs.x[pair[0]] ?? 0) - (fs.x[pair[1]] ?? 0), (fs.y[pair[0]] ?? 0) - (fs.y[pair[1]] ?? 0)) : null;
    const sched = this.eventSchedule;
    const ev = sched?.step(clock.raceTime) ?? { swell: 0, sting: false };
    const intensity = intensityOf({ gapM, lap: fs.lap, laps: this.laps, boost: sched?.boost(clock.raceTime, clock.effectiveRate) ?? 0 });
    for (const c of this.audioCars) {
      const x = fs.x[c.idx] ?? 0, y = fs.y[c.idx] ?? 0;
      cam.worldToStage(x, y, o);
      c.sx = ((o[0] ?? 0) / w) * 2 - 1;
      c.sy = ((o[1] ?? 0) / h) * 2 - 1;
      c.dist = Math.hypot(x - lx, y - ly);
      c.rpm = fs.rpm[c.idx] ?? 0;
      c.thr = fs.thr[c.idx] ?? 0;
      c.gear = fs.gear[c.idx] ?? 0;
      c.speed = fs.speed[c.idx] ?? 0;
      c.running = ((fs.flags[c.idx] ?? 0) & (FLAG.RETIRED | FLAG.HIDDEN)) === 0;
    }
    a.update({
      raceT: clock.raceTime,
      presT: clock.presentationTime,
      mode: clock.mode.kind,
      playing: clock.playing,
      effectiveRate: clock.effectiveRate,
      buffering: this.buffering,
      dragging: this.scrub?.isDragging ?? false,
      hidden: document.hidden,
      listener: { x: lx, y: ly, zoom: 1 / cam.mpp },
      cars: this.audioCars,
      intensity,
      swell: ev.swell,
      sting: ev.sting,
      modelled: this.bundle ? isModelledSpeed(this.bundle.manifest) : false,
    });
  }

  // ───────────────────────────── input ─────────────────────────────

  private bindInput(): void {
    const st = this.el.stage;
    const p: [number, number] = [0, 0];
    // the pending HUD click belongs to one pointer (review TF2): a second finger lifting on the
    // map must not clear it. A new press on the HUD replaces an older pending one (latest wins).
    let hudDown: string | null = null;
    let hudDownId = -1;
    // pointers the map controller owns (T1: one pans, two pinch); keyed by pointerId
    const ctlPtrs = new Set<number>();
    const gl = (): GlCore => {
      if (!this.gl) throw new Error('no gl');
      return this.gl;
    };
    // the browser also fires pointermove after layout changes under a resting cursor: only a real
    // move counts as activity for the chrome auto-hide
    const lastPtr = { x: Number.NaN, y: Number.NaN };
    const moved = (e: PointerEvent): boolean => {
      const m = e.clientX !== lastPtr.x || e.clientY !== lastPtr.y;
      lastPtr.x = e.clientX;
      lastPtr.y = e.clientY;
      return m;
    };
    st.addEventListener('pointermove', (e) => {
      setLastPointerTouch(e.pointerType === 'touch');
      this.stage.toStage(e.clientX, e.clientY, p);
      if (moved(e)) this.chrome?.pointerMoved(p[1], performance.now());
      if (this.pipDrag.move(e.pointerId, e.clientX, e.clientY)) {
        if (hudDownId === e.pointerId) hudDown = null;   // a drag, not a tap: no follow on release
        return;
      }
      const ctl = gl().controller;
      if (ctlPtrs.has(e.pointerId)) {
        ctl.pointerMove(p[0], p[1], e.pointerId);
        if (ctl.isDragging) this.userMoved = true;
        return;
      }
      const r = this.hud?.pointer(p[0], p[1], 'move');
      st.style.cursor = r?.cursor ?? (ctl.accepts(p[0], p[1]) ? 'grab' : 'default');
    });
    st.addEventListener('pointerdown', (e) => {
      setLastPointerTouch(e.pointerType === 'touch');
      this.stage.toStage(e.clientX, e.clientY, p);
      const hit = this.hud?.hitAt(p[0], p[1]);
      if (hit) {
        hudDownId = e.pointerId;
        hudDown = `${hit.owner}/${hit.region.id}`;
        this.hud?.pointer(p[0], p[1], 'down');
        // portrait: the battle cam can also be dragged to another corner (r141)
        const pip = this.layout?.sheet ? this.layout.insets?.inset : undefined;
        if (hit.region.id === 'inset-follow' && e.button === 0 && pip && this.layout && this.stage.layout) {
          this.pipDrag.down(e.pointerId, e.clientX, e.clientY, pip, this.layout.mapRect, this.stage.layout);
          st.setPointerCapture(e.pointerId);
        }
        return;
      }
      if (gl().controller.pointerDown(p[0], p[1], e.button, e.pointerId, e.pointerType === 'touch')) {
        ctlPtrs.add(e.pointerId);
        st.setPointerCapture(e.pointerId);
        st.style.cursor = 'grabbing';
        e.preventDefault();
      }
    });
    const up = (e: PointerEvent, cancel = false): void => {
      this.stage.toStage(e.clientX, e.clientY, p);
      if (this.pipDrag.up(e.pointerId, e.clientX, e.clientY, cancel) && hudDownId === e.pointerId) hudDown = null;
      if (st.hasPointerCapture(e.pointerId) && !ctlPtrs.has(e.pointerId)) st.releasePointerCapture(e.pointerId);
      if (hudDown !== null && hudDownId === e.pointerId) {
        const hit = this.hud?.hitAt(p[0], p[1]);
        if (hit && `${hit.owner}/${hit.region.id}` === hudDown) this.hud?.pointer(p[0], p[1], 'click');
        hudDown = null;
      }
      if (ctlPtrs.delete(e.pointerId)) {
        // a cancelled pointer lifts without a click or a fling (T1)
        if (cancel) gl().controller.pointerCancel(e.pointerId);
        else gl().controller.pointerUp(p[0], p[1], e.button, e.pointerId);
        if (st.hasPointerCapture(e.pointerId)) st.releasePointerCapture(e.pointerId);
        if (ctlPtrs.size === 0) st.style.cursor = 'grab';
      }
    };
    st.addEventListener('pointerup', up);
    st.addEventListener('pointercancel', (e) => {
      up(e, true);
    });
    st.addEventListener('pointerleave', () => {
      this.hud?.pointer(0, 0, 'leave');
      this.chrome?.pointerLeft();
    });
    st.addEventListener(
      'wheel',
      (e) => {
        this.stage.toStage(e.clientX, e.clientY, p);
        if (gl().controller.wheel(p[0], p[1], e.deltaY, e.deltaMode as 0 | 1 | 2)) {
          this.userMoved = true;
          e.preventDefault();
        }
      },
      { passive: false },
    );
    st.addEventListener('dblclick', (e) => {
      this.stage.toStage(e.clientX, e.clientY, p);
      if (gl().controller.dblclick(p[0], p[1])) this.userMoved = true;
    });
    st.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
    this.el.app.addEventListener('pointermove', (e) => {
      if (e.target === st || st.contains(e.target as Node)) return;
      if (moved(e)) this.chrome?.pointerMoved(null, performance.now());
    });

    bindKeyboard(document, {
      togglePlay: () => {
        this.togglePlay();
      },
      toggleFullscreen: () => {
        void this.stage.toggleFullscreen();
      },
      resetView: () => {
        this.resetView();
      },
      unfollow: () => {
        this.setFollow(null);
      },
      jumpSeconds: (dt) => {
        this.seek((this.clock?.raceTime ?? 0) + dt);
      },
      jumpLap: (d) => {
        this.jumpLap(d);
      },
      jumpEvent: (d) => {
        this.jumpEvent(d);
      },
      stepRate: (d) => {
        this.stepRate(d);
      },
      director: () => {
        this.setMode({ kind: 'director' });
      },
      zoom: (f) => {
        gl().controller.zoomBy(f);
        this.userMoved = true;
      },
      toggleChrome: () => {
        this.chrome?.toggleHidden();
      },
      toggleHelp: () => {
        this.help.toggle();
      },
      closeHelp: () => {
        if (!this.help.open) return false;
        this.help.hide();
        return true;
      },
      toggleDebug: () => {
        this.debug.toggle();
      },
      toggleSound: () => {
        this.toggleSound();
      },
      toggleCaptions: () => {
        this.toggleCaptions();
      },
      seekStart: () => {
        this.seek(0);
      },
      seekEnd: () => {
        this.seek(this.tEnd);
      },
      styleKey: (key) => {
        const s = this.style;
        return s?.onKey ? s.onKey(key) : false;
      },
    });
  }

  // ───────────────────────────── deep links ─────────────────────────────

  private deepLink(): DeepLink {
    const s = this.store.get();
    const out: DeepLink = { t: this.clock?.raceTime ?? s.raceTime, mode: s.mode };
    if (s.race) out.race = s.race;
    if (s.follow !== null && this.palette) out.follow = this.palette.driverCode(s.follow);
    const cam = this.gl?.camera;
    if (cam && this.userMoved && !this.presetFly && !cam.isAtPreset() && s.follow === null) {
      const w = [0, 0];
      cam.viewCentreWorld(w);
      out.view = { cx: w[0] ?? 0, cy: w[1] ?? 0, z: 1 / cam.relativeZoom() };
    }
    if (s.playing) out.play = true;
    if (this.takePinned) out.take = this.take + 1;
    if (this.liteSetting !== 'auto') out.lite = this.liteSetting;
    return out;
  }

  private applyDeepLink(d: DeepLink): void {
    // `race` is boot-only (GlCore.useBundle is one-shot): a different race arriving over hashchange
    // (browser Back after a ? menu switch, an edited URL) reloads the page on the new hash.
    if (d.race && d.race !== this.store.get().race) {
      location.reload();
      return;
    }
    if (d.lite && d.lite !== this.liteSetting) this.pickLite(d.lite, false); // Back/Forward and edited URLs
    if (d.mode) this.setMode(d.mode);
    if (d.t !== undefined && Math.abs(d.t - (this.clock?.raceTime ?? 0)) > 0.05) this.seek(d.t);
    if (d.follow && this.palette) {
      const idx = this.palette.carIndex(d.follow);
      if (idx >= 0 && idx !== this.store.get().follow) this.setFollow(idx);
    }
    if (d.view && this.gl) {
      this.gl.camera.setRelativeView(d.view.cx, d.view.cy, 1 / d.view.z);
      this.gl.camera.clampToExtent();
      this.presetFly = null;
      this.userMoved = true;
    }
    // `take=` edited into the hash mid-session applies at once (C10); a value beyond K is ignored
    if (d.take !== undefined && d.take - 1 !== this.take && d.take <= takeCount(this.files.commentary)) {
      this.take = d.take - 1;
      this.takePinned = true;
      this.applyCommentaryFiles();
    }
  }

  // ───────────────────────────── frame ─────────────────────────────

  private frame(forceDt?: number): void {
    const clock = this.clock;
    const fs = this.fs;
    const bundle = this.bundle;
    const gl = this.gl;
    const hud = this.hud;
    const ctx = this.ctx;
    const style = this.style;
    if (!clock || !fs || !bundle || !gl || !hud || !ctx || !this.track) return;
    const now = performance.now();
    const isTick = forceDt === undefined;
    const dtMs = isTick ? now - this.lastWall : 0;
    if (isTick) this.lastWall = now;
    let dt = Math.min(Math.max(dtMs / 1000, 0), 0.25);
    // LITE 30 fps in Director: t snaps to whole presentation frames, so each rendered frame is a video frame
    const snap = isTick && this.lite.rung >= 1 && clock.mode.kind === 'director';
    // whole frames only: a back-to-back rAF (< 20 ms) advances none, and a long gap (first tick after a pause) is capped at 7
    if (snap) dt = dtMs < 20 ? 0 : Math.min(Math.max(1, Math.round(dtMs / (1000 / PRESENTATION_FPS))), 7) / PRESENTATION_FPS;
    // a drag on the scrub bar owns the clock; while the frames at t are not decoded yet the clock holds
    const ready0 = bundle.ready(clock.raceTime);
    if (!this.scrub?.isDragging && ready0) {
      if (clock.playing) this.clocked += dt;
      if (snap && clock.playing) clock.snapToFrame(); // LITE: the absolute position is a whole video frame before advancing
      clock.tick(dt);
      if (snap && clock.playing) clock.snapToFrame(); // also after: a hold, a clamp or a seek never leaves the position off the grid
    }
    const t = clock.raceTime;
    this.presentation = clock.presentationTime;
    const ready = ready0 && bundle.ready(t);
    if (ready !== !this.buffering) {
      this.buffering = !ready;
      if (!ready) void bundle.whenReady(t).catch(() => undefined);
      else {
        // fix 3: a healed buffer resets the backoff for the next episode
        this.retryDelayMs = CHUNK_RETRY_BASE_MS;
        this.nextRetryAt = 0;
      }
    }
    // fix 3: the pump never re-fetches a chunk it gave up on and the whenReady waiters are already
    // rejected: retry() re-queues the FAILED chunks (a no-op when there are none) on a capped
    // backoff, so a dropped chunk heals without a reload
    if (this.buffering && now >= this.nextRetryAt) {
      void bundle.retry().catch(() => undefined); // can reject: "timeline worker is gone" (loader)
      this.nextRetryAt = now + this.retryDelayMs;
      this.retryDelayMs = Math.min(this.retryDelayMs * 2, CHUNK_RETRY_MAX_MS);
    }
    if (ready) {
      const holdT = clock.mode.kind === 'director' ? clock.frameRaceTime : t;
      stateAt(bundle.timeline, this.track, t, fs, { holdT });
      style?.update(fs, ctx);
      // retired bitmask: bit c = car c retired (MAX_CARS = 24 fits; bit 31 would sign-flip)
      let mask = 0;
      for (let c = 0; c < fs.flags.length && c < 31; c++) if (gone(fs.flags[c] ?? 0)) mask |= 1 << c;
      if (mask !== this.retiredMask) {
        this.retiredMask = mask;
        this.transport?.setRetired(mask);
        const f = this.store.get().follow;
        if (f !== null && mask & (1 << f)) this.setFollow(null);
      }
    }
    this.captions?.update({
      presT: clock.presentationTime,
      mode: clock.mode.kind,
      age: (tr) => clock.presentationAge(tr),
      audio: this.soundOn ? this.audio?.state() : undefined,
    });
    this.updateAudio(clock, fs);
    gl.frame(dt, this.presentation);
    if (this.presetFly && !gl.camera.flying) {
      this.presetFly = null;
      this.hashWriter.request();
    }
    // follow can be released by the controller (drag, Esc): keep the store in sync
    const camFollow = gl.camera.following;
    if (camFollow !== this.store.get().follow) {
      this.store.set({ follow: camFollow });
      this.transport?.setFollow(camFollow);
    }
    // GL only when the picture can have changed: race time, camera, tiles, layout, play state (the
    // paused trail window), or 2 Hz anyway
    const tilesV = this.tilesVersion();
    const camV = gl.camera.version;
    if (this.glDirty || t !== this.lastRender.t || camV !== this.lastRender.cam || tilesV !== this.lastRender.tiles || clock.playing !== this.lastRender.playing || now - this.lastRender.wall > 500) {
      gl.render();
      this.renders++;
      this.glDirty = false;
      this.lastRender.t = t;
      this.lastRender.cam = camV;
      this.lastRender.tiles = tilesV;
      this.lastRender.wall = now;
      this.lastRender.playing = clock.playing;
    }
    hud.flush();
    // chrome (the speed label)
    const playing = clock.playing;
    const mode = clock.mode;
    const speed = this.buffering ? 'BUFFERING' : !playing ? 'PAUSE' : mode.kind === 'director' ? clock.warp.speedLabel(clock.videoTime) : `${String(mode.rate)}X`;
    this.transport?.update({ playing, mode, clock: fmtRaceClock(t + suspensionShift(this.suspensions, t)), lap: Math.min(Math.max(fs.lap, 1), this.laps), laps: this.laps, speed });
    this.scrub?.setTime(t);
    this.chrome?.tick(now, playing, this.scrub?.isDragging ?? false);
    if (isTick) {
      this.frames++;
      if (this.stallMs > 0) {
        const until = performance.now() + this.stallMs;
        while (performance.now() < until) {
          // busy-wait (debug only)
        }
      }
      this.store.set({ raceTime: t, playing });
      if (dtMs > 0) {
        this.stats.push(dtMs);
        // the 30 fps budget applies while the platform caps rAF or LITE caps us
        const budget = this.lite.rung >= 1 ? BUDGET_30 : this.rafBudget.budgetMs;
        const gov = this.governor;
        if (gov && !gov.isPinned) {
          gov.setBudget(budget);
          gov.sample(dtMs, now / 1000);
        }
        const rg = gov?.r ?? 1;
        const li = this.liteIn;
        li.budgetMs = budget;
        li.atCap = gov?.atCap ?? true;
        li.lowerCost = (this.effectiveR() / rg) ** 2;
        this.lite.sample(dtMs, now / 1000, li);
        this.syncLite();
        const r = this.effectiveR();
        if (r !== this.renderScale) {
          this.renderScale = r;
          this.stage.setRenderScale();
        }
      }
      this.rafBudget.pushWork(performance.now() - now);
      this.debug.update(now, () => this.debugText());
    }
  }

  /**
   * Tile caches of the GL core (read-only peek at GlCore's private caches until it exposes tile
   * stats publicly): version sum (for render on demand) and idle state (for tests).
   */
  private tileCaches(): { version: number; capacity: number; stats(): { loading: number; queued: number; ready: number; pages: number } }[] {
    const m = (this.gl as unknown as { caches?: Map<string, { version: number; capacity: number; stats(): { loading: number; queued: number; ready: number; pages: number } }> } | null)?.caches;
    return m ? [...m.values()] : [];
  }

  private tilesVersion(): number {
    let v = 0;
    const m = (this.gl as unknown as { caches?: Map<string, { version: number }> } | null)?.caches;
    if (m) for (const c of m.values()) v += c.version;
    return v;
  }

  private debugText(): string {
    const l = this.stage.layout;
    const c = this.clock;
    const cam = this.gl?.camera;
    const mean = this.stats.mean();
    const p95 = this.stats.percentile(0.95);
    const scaleStr = l ? `f ${l.scale.k.toFixed(3)}` : '?';
    const lines = [
      `fps      ${(mean > 0 ? 1000 / mean : 0).toFixed(1)}  (p95 ${p95.toFixed(1)} ms, mean ${mean.toFixed(1)} ms)`,
      `lite     rung ${String(this.lite.rung)} (${this.lite.mode})  budget ${(this.lite.rung >= 1 ? BUDGET_30 : this.rafBudget.budgetMs).toFixed(1)} ms  raf ${this.rafBudget.intervalMs.toFixed(1)} ms${this.lite.platformCapped ? ' (platform cap)' : ''}`,
      `scale    ${scaleStr}  r ${this.renderScale.toFixed(2)}${this.governor?.isPinned ? ' (pinned)' : ''}  stage ${String(l?.scale.stage.w)}×${String(l?.scale.stage.h)}`,
      `device   ${String(l?.scale.device.w)}×${String(l?.scale.device.h)}  dpr ${String(l?.dpr)}  relayout ${this.debugRelayoutMs.toFixed(1)} ms`,
      `camera   mpp ${cam ? cam.mpp.toFixed(3) : '?'} (preset ${cam ? cam.presetMpp.toFixed(3) : '?'})  centre ${cam ? `${cam.center[0].toFixed(1)}, ${cam.center[1].toFixed(1)}` : '?'}  follow ${String(this.store.get().follow)}`,
      `time     race ${c ? c.raceTime.toFixed(2) : '?'} s  video ${c ? c.videoTime.toFixed(3) : '?'} s  frame ${c ? String(c.videoFrame) : '?'}  ${c ? (c.mode.kind === 'director' ? 'DIRECTOR' : `${String(c.mode.rate)}X`) : ''}`,
      `hud      drawn ${String(this.hud?.stats.drawn)}  text cache ${this.hud ? `${String(this.hud.text.size)} runs ${(this.hud.text.bytes / 1048576).toFixed(1)} MB` : ''}`,
      `tiles    ${this.tileCaches().map((t) => `${String(t.capacity)} slots, ${String(t.stats().ready)} ready, ${String(t.stats().pages)} pages`).join(' · ') || 'none'}`,
    ];
    return lines.join('\n');
  }

  // ───────────────────────────── test probe ─────────────────────────────

  private installProbe(): void {
    const probe: PlayerProbe = {
      state: () => {
        const c = this.clock;
        const cam = this.gl?.camera;
        const l = this.stage.layout;
        return {
          style: this.style?.id,
          raceTime: c?.raceTime,
          videoTime: c?.videoTime,
          videoFrame: c?.videoFrame,
          playing: c?.playing,
          mode: c?.mode,
          follow: this.store.get().follow,
          mpp: cam?.mpp,
          presetMpp: cam?.presetMpp,
          relZoom: cam?.relativeZoom(),
          atPreset: cam?.isAtPreset(),
          centre: cam ? [...cam.center] : null,
          centerPx: cam ? [cam.cpx, cam.cpy] : null,
          rect: cam ? { ...cam.rect } : null,
          k: l?.scale.k,
          r: this.renderScale,
          lite: this.lite.rung,
          stage: l?.scale.stage,
          mapRect: this.layout?.mapRect,
          panels: this.layout?.panels,
          sheet: { state: this.sheet.state, tab: this.sheet.tab, scroll: this.sheet.scroll, rect: this.layout?.sheet?.rect ?? null },
          inputRect: this.layout?.inputRect,
          relayouts: this.stage.relayouts,
        };
      },
      carStage: (car) => {
        const fs = this.fs;
        const cam = this.gl?.camera;
        if (!fs || !cam) return null;
        const o = [0, 0];
        cam.worldToStage(fs.x[car] ?? 0, fs.y[car] ?? 0, o);
        return [o[0] ?? 0, o[1] ?? 0];
      },
      worldToStage: (x, y) => {
        const o = [0, 0];
        this.gl?.camera.worldToStage(x, y, o);
        return [o[0] ?? 0, o[1] ?? 0];
      },
      stageToWorld: (x, y) => {
        const o = [0, 0];
        this.gl?.camera.stageToWorld(x, y, o);
        return [o[0] ?? 0, o[1] ?? 0];
      },
      seek: (t) => {
        this.seek(t);
      },
      seekVideo: (v) => {
        const c = this.clock;
        if (!c) return;
        c.seekVideo(v);
        void this.bundle?.whenReady(c.raceTime).catch(() => undefined);
        if (this.ctx) this.style?.onSeek?.(c.raceTime, this.ctx);
        this.store.set({ raceTime: c.raceTime });
      },
      setPlaying: (p) => {
        this.setPlaying(p);
      },
      setMode: (m) => {
        this.setMode(m);
      },
      follow: (c) => {
        this.setFollow(c);
      },
      audio: () => ({
        sound: this.soundOn,
        start: this.startScreen?.state ?? 'hidden',
        ambience: this.audio?.state().ambience ?? false,
        cc: this.captions?.on ?? false,
        radio: this.captions?.state.radio?.id ?? null,
        commentary: this.captions?.state.commentary?.id ?? null,
      }),
      frameMs: () => ({ mean: this.stats.mean(), p95: this.stats.percentile(0.95), frames: this.stats.frames }),
      resetFrameStats: () => {
        this.stats.reset();
      },
      signals: () => {
        const cam = this.gl?.camera;
        const caches = this.tileCaches();
        const tilesIdle = caches.every((c) => {
          const st = c.stats();
          return st.loading === 0 && st.queued === 0;
        });
        return {
          frames: this.frames,
          renders: this.renders,
          idleCount: this.idleCount,
          framesSinceCameraChange: this.frames - this.camChange.frame,
          msSinceCameraChange: performance.now() - this.camChange.wall,
          flying: (cam?.flying ?? false) || this.presetFly !== null,
          tilesIdle,
          clocked: this.clocked,
          buffering: this.buffering,
        };
      },
      resources: () => {
        const sc = this.gl?.scene;
        const eng = this.gl?.engine;
        const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
        return {
          meshes: sc?.meshes.length ?? 0,
          materials: sc?.materials.length ?? 0,
          textures: sc?.textures.length ?? 0,
          engineTextures: eng?.getLoadedTexturesCache().length ?? 0,
          activeCameras: sc?.activeCameras?.length ?? 0,
          hudPanels: this.layout ? Object.keys(this.layout.panels).filter((id) => this.hud?.hasPanel(id)).length : 0,
          textRuns: this.hud?.text.size ?? 0,
          heap: mem ? mem.usedJSHeapSize : null,
        };
      },
    };
    // the view installs its own accessors on the same global (frame, inset, caption, ...): merge
    const w = window as unknown as { __undercut?: object };
    w.__undercut = Object.assign(w.__undercut ?? {}, probe);
  }
}

